import React, { useState } from 'react';
import { 
  Brain, 
  Stethoscope, 
  Users, 
  Clock, 
  MapPin, 
  FileText, 
  Phone, 
  CheckCircle,
  AlertCircle,
  Star
} from 'lucide-react';

const PsychiatryPage: React.FC = () => {
  const [selectedAge, setSelectedAge] = useState<'adult' | 'child'>('adult');

  const psychiatrists = [
    {
      name: "Dr. Sheikh Said Ahmed",
      title: "MD/Specialty Doctor, General Adult Psychiatry",
      specialties: ["Adult ADHD", "Perinatal assessments", "Breastfeeding advice"],
      note: "No autism assessments"
    },
    {
      name: "Dr. Omar Ahmad Choudhry", 
      title: "MD & Child & Adolescent Consultant Psychiatrist",
      specialties: ["Child & Adolescent Psychiatry", "Youth Mental Health"]
    },
    {
      name: "Dr. Robin Garai",
      title: "MD & ADHD Consultant Psychiatrist", 
      specialties: ["ADHD/ADD Assessments", "ADHD Treatment"]
    },
    {
      name: "Dr Vikram Tanwani",
      title: "MD/Specialty Doctor, General Adult Psychiatry",
      specialties: ["General Adult Psychiatry", "Mental Health Assessments"]
    },
    {
      name: "Dr Islam Mohammed Heliel",
      title: "MD & Consultant in General Adult Psychiatry",
      specialties: ["General Adult Psychiatry", "Substance misuse", "Neuropsychiatry"]
    }
  ];

  const mainServices = [
    {
      id: 'adhd',
      title: 'ADHD/ADD Comprehensive Assessment',
      duration: '60 minutes',
      description: 'Initial/triage/rating scales + diagnostic assessment + diagnostic report. No GP referral needed.',
      pricing: {
        remote: { adult: 935, child: 1400 },
        faceToFaceA: { adult: 965, child: 1430 },
        faceToFaceB: { adult: 965, child: 1430, additional: true }
      },
      comparison: 'Psychiatry UK: Adult £950, Child £1,420',
      includes: ['Diagnostic assessment', 'Written report within 7 days', 'Treatment plan discussion', 'Next steps guidance']
    },
    {
      id: 'autism',
      title: 'ASD (Autism) Comprehensive Assessment',
      duration: 'Stage 1A & 1B, consultation, document analysis, ADOS assessment',
      description: 'Complete autism assessment with diagnostic report, personalised recommendations and 30 minutes follow-up.',
      pricing: {
        remote: { adult: 1930, child: 1680 },
        faceToFaceA: { adult: 1960, child: 1710 },
        faceToFaceB: { adult: 1960, child: 1710, additional: true }
      },
      comparison: 'Psychiatry UK: Adult £1,950, Child £1,700 | National Autistic Society: £1,800-£3,800',
      includes: ['ADOS assessment', 'Document analysis', 'Diagnostic report', '30-minute follow-up', 'Personalised recommendations']
    },
    {
      id: 'combined',
      title: 'Combined ADHD & Autism Assessment',
      duration: 'Extended comprehensive assessment',
      description: 'Combined assessment with diagnostic report and personalised treatment plan. Excludes ADHD medication services.',
      pricing: {
        remote: { adult: 2665, child: 2880 },
        faceToFaceA: { adult: 2695, child: 2910 },
        faceToFaceB: { adult: 2695, child: 2910, additional: true }
      },
      savings: 'Save £200 compared to separate assessments',
      includes: ['Combined ADHD & autism assessment', 'Comprehensive diagnostic report', 'Personalised treatment plan', 'Cost savings vs separate assessments']
    }
  ];

  const additionalServices = [
    {
      title: 'Diagnostic Neuropsychiatric Assessment',
      description: 'Evaluate brain function with cognitive tests, interviews, medical record review (10-15 hours)',
      pricing: { remote: 210, faceToFaceA: 240, faceToFaceB: 240, unit: 'per hour' }
    },
    {
      title: 'Medico-legal Assessment with Neuropsychiatrist',
      description: 'Legal assessment with neuropsychiatrist for court/legal proceedings',
      pricing: { remote: 350, faceToFaceA: 380, faceToFaceB: 380, unit: 'per hour', additional: true }
    },
    {
      title: 'Mental Capacity Assessment (Neuropsychiatrist)',
      description: 'Adult capacity assessment by neuropsychiatrist',
      pricing: { remote: 550, faceToFaceA: 600, faceToFaceB: 600, additional: true }
    },
    {
      title: 'Diagnostic Review / Second Opinion',
      description: 'Case review, collateral, recommendations without ongoing management',
      pricing: { remote: 450, faceToFaceA: 500, faceToFaceB: 500, additional: true }
    },
    {
      title: 'Mental Health Assessment',
      description: 'Full initial assessment + report (~90 minutes). No prescribing.',
      pricing: { remote: { adult: 500, child: 400 }, faceToFaceA: { adult: 550, child: 450 }, faceToFaceB: { adult: 550, child: 450, additional: true } }
    },
    {
      title: 'Mental Capacity Assessment (Psychiatrist)',
      description: 'Child (age/context dependent) or Adult capacity assessment for treatment, finances, residence, etc.',
      pricing: { remote: 450, faceToFaceA: 500, faceToFaceB: 500, additional: true }
    },
    {
      title: 'Occupational Health Assessment',
      description: 'Fitness to work, adjustments, return-to-work plans with report',
      pricing: { remote: 700, faceToFaceA: 750, faceToFaceB: 750, additional: true }
    },
    {
      title: 'Risk Assessment',
      description: 'Suicide/self-harm/violence/risk to others evaluation',
      pricing: { remote: 500, faceToFaceA: 550, faceToFaceB: 550, additional: true }
    },
    {
      title: 'Perinatal Assessment',
      description: 'For antenatal/postnatal women with mental health conditions + breastfeeding advice',
      pricing: { remote: 350, faceToFaceA: 400, faceToFaceB: 400, additional: true, minimum: true }
    },
    {
      title: 'Follow-Up Review Appointment',
      description: 'With psychiatrist + update letter to GP with/without prescription (30-45 minutes)',
      pricing: { remote: 170, faceToFaceA: 200, faceToFaceB: 200, additional: true }
    },
    {
      title: 'Medico-Legal / Court Report',
      description: 'Capacity, forensic, immigration, asylum, etc.',
      pricing: { remote: 250, faceToFaceA: 280, faceToFaceB: 280, unit: 'per hour', additional: true }
    }
  ];

  const adhdPostServices = [
    {
      title: 'Optional Medication Assessment',
      description: '30-45 minutes appointment for medication evaluation',
      price: 75,
      type: 'remote'
    },
    {
      title: 'Medication Titration (12-week)',
      description: 'Complete titration process with BP monitor device',
      price: 730,
      additionalCost: '£14-£19 for BP Monitor',
      comparison: 'Psychiatry UK: £750 + £15-£20 for BP Monitor'
    },
    {
      title: 'Additional 4-week Titration',
      description: 'Extended titration package',
      price: 190,
      frequency: 'every 4 weeks',
      comparison: 'Psychiatry UK: £200 every 4 weeks'
    },
    {
      title: 'Medication Review',
      description: 'Regular medication review appointments',
      price: 180,
      comparison: 'Psychiatry UK: £190'
    },
    {
      title: 'Annual Review (Shared Care)',
      description: '30-45 minutes annual review for GP shared care patients',
      price: 170,
      frequency: 'per year',
      comparison: 'Psychiatry UK: £180/year'
    },
    {
      title: 'Annual Review (In-house)',
      description: '30-45 minutes review + 12 prescriptions for in-house patients',
      price: 460,
      frequency: 'per year',
      comparison: 'Psychiatry UK: £480/year'
    }
  ];

  const formatPrice = (price: number) => `£${price.toLocaleString()}`;

  const getPricing = (service: any, type: 'remote' | 'faceToFaceA' | 'faceToFaceB') => {
    const pricing = service.pricing[type];
    if (typeof pricing === 'object' && 'adult' in pricing) {
      return selectedAge === 'adult' ? pricing.adult : pricing.child;
    }
    return pricing;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Hero Section with Medical Professional Image */}
        <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg mb-12 overflow-hidden">
          <div className="flex flex-col lg:flex-row items-center">
            {/* Left Content */}
            <div className="lg:w-1/2 p-8 lg:p-12">
              <div className="flex items-center gap-3 mb-6">
                <Brain className="h-10 w-10 text-blue-600" />
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                  Psychiatry Diagnostics
                </h1>
              </div>
              <h2 className="text-xl lg:text-2xl text-blue-600 font-semibold mb-4">
                ASD, ADHD, DYSLEXIA & NEURODIVERSE ASSESSMENTS
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Delivered by Specialist and Consultant Psychiatrists and Neuropsychiatrists
              </p>
              
              {/* Discount Notice */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-green-800 font-semibold text-sm">
                    UK residents with evidence of Universal Credit Benefit may get 10-15% discount 
                    when booked personally or through UK Social Services, CAMHS, NHS or GP
                  </p>
                </div>
              </div>
            </div>
            
            {/* Right Content - Medical Professional Image */}
            <div className="lg:w-1/2 p-8">
              <div className="relative rounded-lg overflow-hidden shadow-xl">
                {/* Placeholder for the medical professional image */}
                <div className="aspect-[4/3] bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Stethoscope className="h-12 w-12 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Medical Professional</h3>
                    <p className="text-sm text-gray-600">Expert psychiatric assessments and diagnostics</p>
                  </div>
                </div>
                {/* In real implementation, this would be: */}
                {/* <img 
                  src={MedicalProfessionalImage} 
                  alt="Medical professional with clipboard and stethoscope"
                  className="w-full h-full object-cover"
                /> */}
              </div>
            </div>
          </div>
        </div>

        {/* Age Selection */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-2 shadow-md">
            <div className="flex">
              <button
                onClick={() => setSelectedAge('adult')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  selectedAge === 'adult'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Adult (18+)
              </button>
              <button
                onClick={() => setSelectedAge('child')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  selectedAge === 'child'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Child & Adolescent (Under 18)
              </button>
            </div>
          </div>
        </div>

        {/* Main Services */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Primary Assessment Services
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {mainServices.map((service) => (
              <div 
                key={service.id} 
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Stethoscope className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">{service.duration}</span>
                  </div>
                  
                  <p className="text-gray-700 mb-6">{service.description}</p>
                  
                  {/* Pricing */}
                  <div className="space-y-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">Remote Assessment</h4>
                      <p className="text-2xl font-bold text-blue-900">
                        {formatPrice(getPricing(service, 'remote'))}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Face-to-Face (Option A)</h4>
                      <p className="text-xl font-bold text-gray-900">
                        {formatPrice(getPricing(service, 'faceToFaceA'))}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">At our clinics</p>
                    </div>
                    
                    <div className="bg-orange-50 rounded-lg p-4">
                      <h4 className="font-semibold text-orange-800 mb-2">Face-to-Face (Option B)</h4>
                      <p className="text-xl font-bold text-orange-900">
                        {formatPrice(getPricing(service, 'faceToFaceB'))} + travel
                      </p>
                      <p className="text-xs text-orange-600 mt-1">At your location</p>
                    </div>
                  </div>
                  
                  {service.comparison && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                      <p className="text-xs text-yellow-800">
                        <strong>Price comparison:</strong> {service.comparison}
                      </p>
                    </div>
                  )}
                  
                  {service.savings && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <p className="text-xs text-green-800">
                        <strong>💰 {service.savings}</strong>
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">Includes:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {service.includes.map((item, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ADHD Post-Diagnosis Services */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            ADHD Post-Diagnosis Services
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {adhdPostServices.map((service, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-700 text-sm mb-4">{service.description}</p>
                  
                  <div className="space-y-2">
                    <p className="text-2xl font-bold text-blue-600">
                      {formatPrice(service.price)}
                      {service.frequency && <span className="text-sm text-gray-600"> {service.frequency}</span>}
                    </p>
                    
                    {service.additionalCost && (
                      <p className="text-sm text-orange-600">+ {service.additionalCost}</p>
                    )}
                    
                    {service.comparison && (
                      <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        {service.comparison}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-blue-800 mb-3">Medication Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-white p-4 rounded">
                  <h4 className="font-semibold text-green-800">Shared Care</h4>
                  <p className="text-gray-700">GP takes over prescriptions after stabilization (up to 12 weeks titration)</p>
                </div>
                <div className="bg-white p-4 rounded">
                  <h4 className="font-semibold text-orange-800">Signposting</h4>
                  <p className="text-gray-700">Transferred to private medication company for ongoing care</p>
                </div>
                <div className="bg-white p-4 rounded">
                  <h4 className="font-semibold text-blue-800">In-house</h4>
                  <p className="text-gray-700">HealthCity handles all future prescriptions and reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Services */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Additional Psychiatric Services
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {additionalServices.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-700 text-sm mb-4">{service.description}</p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Remote:</span>
                    <span className="font-semibold text-blue-600">
                      {typeof service.pricing.remote === 'object' 
                        ? formatPrice(selectedAge === 'adult' ? service.pricing.remote.adult : service.pricing.remote.child)
                        : formatPrice(service.pricing.remote)
                      }
                      {service.pricing.unit && ` ${service.pricing.unit}`}
                      {service.pricing.minimum && ' (minimum)'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Face-to-Face (Option A):</span>
                    <span className="font-semibold text-gray-900">
                      {typeof service.pricing.faceToFaceA === 'object'
                        ? formatPrice(selectedAge === 'adult' ? service.pricing.faceToFaceA.adult : service.pricing.faceToFaceA.child)
                        : formatPrice(service.pricing.faceToFaceA)
                      }
                      {service.pricing.unit && ` ${service.pricing.unit}`}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Face-to-Face (Option B):</span>
                    <span className="font-semibold text-orange-600">
                      {typeof service.pricing.faceToFaceB === 'object'
                        ? formatPrice(selectedAge === 'adult' ? service.pricing.faceToFaceB.adult : service.pricing.faceToFaceB.child)
                        : formatPrice(service.pricing.faceToFaceB)
                      }
                      {service.pricing.unit && ` ${service.pricing.unit}`}
                      {service.pricing.additional && ' + travel'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Our Psychiatrists */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Specialist Psychiatrists
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {psychiatrists.map((doctor, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="h-6 w-6 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">{doctor.name}</h3>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">{doctor.title}</p>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900">Specialties:</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {doctor.specialties.map((specialty, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <Star className="h-3 w-3 text-yellow-500" />
                        {specialty}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {doctor.note && (
                  <div className="mt-4 bg-orange-50 border border-orange-200 rounded p-3">
                    <p className="text-xs text-orange-800">
                      <AlertCircle className="h-3 w-3 inline mr-1" />
                      {doctor.note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Important Notes */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Important Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-bold text-gray-900">Reports & Delivery</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-1" />
                  <span>Reports produced within 10 days (inclusive of weekends)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-1" />
                  <span>Delivered to referrer or client</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-1" />
                  <span>All relevant medical records required for assessment</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="h-6 w-6 text-orange-600" />
                <h3 className="text-lg font-bold text-gray-900">Price Exclusions</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-1" />
                  <span>Court appearances and Expert Witness services</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-1" />
                  <span>Tribunal hearings and legal proceedings</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-500 mt-1" />
                  <span>Additional meetings beyond standard assessment</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Other Services & Contact */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need Other Psychiatric Services?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Tell us about additional psychiatric services you're looking for and we'll get back to you 
              with our proposed pricing and payment terms.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900">Quick Services</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Travel Support Letter</span>
                  <span className="font-semibold text-blue-600">£50.00</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Short Update Reports/Letters</span>
                  <span className="font-semibold text-blue-600">£150.00</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Follow-Up Review (Remote)</span>
                  <span className="font-semibold text-blue-600">£170.00</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Alcohol & Substance Misuse Assessment</span>
                  <span className="font-semibold text-blue-600">£350.00+ (minimum)</span>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-blue-800 mb-4">Get in Touch</h3>
              <p className="text-blue-700 mb-6">
                Contact us to discuss your specific psychiatric assessment needs and receive a tailored quote.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-700">0333 800 3006</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-700">0333 789 0012</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-700">Canary Wharf, London</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PsychiatryPage;